/**
 * Single fetch wrapper for the Cloudflare Worker API.
 *
 * There is no Vite proxy. Point the SPA at the Worker with VITE_API_BASE:
 *   VITE_API_BASE=http://localhost:8787 npm run dev:web         (dev)
 *   VITE_API_BASE=https://nim-ai-pr-reviewer.<account>.workers.dev npm run build:web  (prod)
 *
 * The current org id (set by OrgProvider) is attached as X-Org-Id on every
 * request so org-scoped Worker routes resolve the right tenant.
 */
import { getCurrentOrgId } from './current-org';

export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export async function api<T = unknown>(path: string, init: RequestInit = {}, orgId?: string | null): Promise<T> {
  // An explicit orgId wins over the module-level bridge so org-scoped fetches
  // (capabilities) hit the right tenant even when the OrgProvider effect that
  // syncs the bridge hasn't fired yet in this commit.
  const headerOrgId = orgId ?? getCurrentOrgId();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(headerOrgId ? { 'X-Org-Id': headerOrgId } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`API ${res.status} ${path}: ${body}`);
  }
  return res.json() as Promise<T>;
}
